import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { keyframes } from '@mui/system';
import LoadingSpinner from '../components/LoadingSpinner';
import Contact from '../components/Contact';
import simpliVityImage from '../assets/images/hpe_servers/hpe-simplivity-380h-server.jpg';
import greenLakeImage from '../assets/images/hpe_servers/HPE CloudOps Software.png';
import storeOnceImage from '../assets/images/hpe_servers/hpe-storage-default.jpg';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  CloudQueue as CloudIcon,
  Storage as StorageIcon,
  Backup as BackupIcon,
  Hub as HubIcon,
} from '@mui/icons-material';

const pulsate = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(25, 118, 210, 0.5),
                0 0 30px rgba(25, 118, 210, 0.3);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 8px 30px rgba(25, 118, 210, 0.8),
                0 0 50px rgba(25, 118, 210, 0.6),
                inset 0 0 20px rgba(255, 255, 255, 0.3);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 20px rgba(25, 118, 210, 0.5),
                0 0 30px rgba(25, 118, 210, 0.3);
  }
`;

const ServiceDetails: React.FC = () => {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 200);
    return () => clearTimeout(timer);
  }, [serviceId]);

  const serviceData: { [key: string]: any } = {
    'virtualization': {
      title: 'Virtualization Solutions',
      description: 'Transform your IT infrastructure with enterprise-grade virtualization for improved efficiency, scalability, and cost savings.',
      overview: 'Our virtualization solutions help organizations consolidate their physical infrastructure, reduce operational costs by up to 70%, and improve resource utilization. We design and implement tailored virtualization platforms that align with your business objectives and growth plans. Whether you\'re migrating from physical servers or upgrading existing virtual infrastructure, our expert team ensures a smooth transition with minimal disruption to your operations.',
      image: simpliVityImage,
      process: [
        'Comprehensive infrastructure assessment and capacity planning',
        'Virtualization platform design and architecture blueprint',
        'Vendor-neutral hardware and software recommendations',
        'Professional platform deployment and configuration',
        'Seamless virtual machine migration with zero downtime',
        'Performance optimization and load balancing',
        'Complete documentation and knowledge transfer'
      ],
      benefits: [
        'Reduce hardware and datacenter costs by up to 70%',
        'Improve server utilization from 15% to 80%+',
        'Provision new servers in minutes instead of weeks',
        'Enable rapid disaster recovery with VM snapshots',
        'Centralized management of your entire infrastructure',
        'Reduce energy consumption and cooling costs',
        'Improve business agility and time to market'
      ],
      deliverables: [
        'Detailed virtualization architecture design document',
        'Fully configured and optimized virtualization platform',
        'Comprehensive migration execution plan and support',
        'Performance baseline and optimization report',
        'Complete administrator training and certification prep',
        'Ongoing support and maintenance guidelines'
      ],
      timeline: '4-8 weeks',
      icon: <HubIcon />
    },
    'private-cloud': {
      title: 'Private Cloud Infrastructure',
      description: 'Build a secure, scalable private cloud environment tailored to your organization\'s specific requirements.',
      overview: 'Our private cloud solutions deliver the flexibility and scalability of public cloud with the security and control of on-premises infrastructure. We design and implement comprehensive private cloud environments using industry-leading technologies, enabling you to achieve cloud economics while maintaining complete control over your data and compliance requirements. Perfect for regulated industries and organizations requiring data sovereignty.',
      image: greenLakeImage,
      process: [
        'Comprehensive business requirements and workload analysis',
        'Cloud-native architecture design and planning',
        'Infrastructure sizing and procurement guidance',
        'Private cloud platform deployment and integration',
        'Advanced automation and orchestration setup',
        'Security hardening and compliance validation',
        'User onboarding and self-service portal configuration'
      ],
      benefits: [
        'Complete control over data security and governance',
        'Predictable costs without cloud vendor lock-in',
        'Meet strict regulatory and compliance requirements',
        'Self-service provisioning for faster deployment',
        'Seamless integration with existing enterprise systems',
        'Reduced capital expenditure with flexible consumption models',
        'Hybrid cloud ready architecture'
      ],
      deliverables: [
        'Comprehensive private cloud architecture blueprint',
        'Fully operational and production-ready cloud platform',
        'Custom automation and orchestration workflows',
        'Security policies and compliance documentation',
        'Self-service portal for end users',
        'Complete administrator and user training programs'
      ],
      timeline: '6-12 weeks',
      icon: <CloudIcon />
    },
    'disaster-recovery': {
      title: 'Disaster Recovery Planning',
      description: 'Comprehensive business continuity solutions ensuring rapid recovery from any disruption or disaster scenario.',
      overview: 'Our disaster recovery services provide peace of mind with robust, tested solutions designed to minimize downtime and data loss. We implement automated failover and recovery systems that ensure your critical business operations remain available even during catastrophic failures. With RPO measured in seconds and RTO in minutes, your business can survive any disaster from hardware failure to natural catastrophes.',
      image: storeOnceImage,
      process: [
        'Comprehensive business impact analysis and risk assessment',
        'Define Recovery Time Objectives (RTO) and Recovery Point Objectives (RPO)',
        'Design redundant DR architecture with multiple recovery tiers',
        'Implement automated replication and failover mechanisms',
        'Configure continuous data protection and snapshots',
        'Regular DR testing with detailed validation procedures',
        'Create comprehensive runbooks and recovery documentation'
      ],
      benefits: [
        'Minimize business downtime to minutes instead of days',
        'Protect against data loss with near-zero RPO',
        'Meet business continuity and compliance requirements',
        'Automated orchestrated failover and failback',
        'Regular testing ensures recovery confidence',
        'Reduce insurance premiums with proven DR capability',
        'Peace of mind for business leadership'
      ],
      deliverables: [
        'Comprehensive disaster recovery plan and procedures',
        'Fully configured replication and failover systems',
        'Automated orchestration and recovery workflows',
        'Detailed recovery runbooks for IT operations',
        'DR test results and validation reports',
        'Complete training for IT staff and stakeholders',
        'Ongoing support and quarterly DR testing'
      ],
      timeline: '6-10 weeks',
      icon: <BackupIcon />
    },
    'data-backup': {
      title: 'Enterprise Data Backup',
      description: 'Rock-solid backup solutions protecting your critical data with advanced encryption, automation, and rapid recovery.',
      overview: 'Our enterprise backup solutions ensure your data is protected with multiple layers of redundancy and security. We implement modern backup strategies including immutable backups, ransomware protection, and cloud integration for maximum data resilience. Following industry best practices like the 3-2-1-1-0 rule, we ensure your organization can recover from any data loss scenario, whether caused by hardware failure, human error, or ransomware attacks.',
      image: storeOnceImage,
      process: [
        'Complete data inventory and classification by criticality',
        'Backup strategy design following 3-2-1-1-0 rule',
        'High-performance backup infrastructure deployment',
        'Granular backup job configuration and scheduling',
        'Implementation of encryption at rest and in transit',
        'Immutable backup repositories for ransomware protection',
        'Regular restore testing and validation procedures',
        'Integration with cloud storage for off-site copies'
      ],
      benefits: [
        'Comprehensive protection against data loss and ransomware',
        'Fast and reliable recovery with instant VM restore',
        'Fully automated backup operations with monitoring',
        'Achieve compliance with data protection regulations',
        'Reduce storage costs by 20:1 with deduplication',
        'Air-gapped immutable backups prevent tampering',
        '99.999%* backup success rate with automatic retry'
      ],
      deliverables: [
        'Comprehensive backup architecture and policy design',
        'Fully configured backup infrastructure and repositories',
        'Encrypted and immutable backup storage',
        'Automated backup job schedules and policies',
        'Regular recovery testing and validation reports',
        'Detailed backup administration and recovery guide',
        '24/7 monitoring dashboard and alerting'
      ],
      timeline: '3-6 weeks',
      icon: <StorageIcon />
    }
  };

  const service = serviceData[serviceId || ''];

  if (loading) {
    return (
      <Box sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <LoadingSpinner message="Loading service details..." />
        </Container>
      </Box>
    );
  }

  if (!service) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom>Service Not Found</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This service doesn't exist or has been moved.
          </Typography>
          <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
            Go Back
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3 }}
        >
          Back to Services
        </Button>

        <Paper sx={{ p: { xs: 3, md: 6 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ color: 'primary.main', mr: 2 }}>
              {service.icon}
            </Box>
            <Typography variant="h3" component="h1">
              {service.title}
            </Typography>
          </Box>

          <Typography variant="h6" color="text.secondary" paragraph>
            {service.description}
          </Typography>

          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            {service.overview}
          </Typography>

          {service.image && (
            <Box
              sx={{
                mb: 4,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            >
              <img
                src={service.image}
                alt={`${service.title} - Enterprise Infrastructure`}
                loading="lazy"
                style={{
                  width: '100%',
                  height: 'auto',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            </Box>
          )}

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Our Process
                  </Typography>
                  <List>
                    {service.process.map((step: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.875rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {index + 1}
                          </Box>
                        </ListItemIcon>
                        <ListItemText primary={step} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Key Benefits
                  </Typography>
                  <List>
                    {service.benefits.map((benefit: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <CheckIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={benefit} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>



            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Deliverables
                  </Typography>
                  <List>
                    {service.deliverables.map((deliverable: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={deliverable} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    Timeline
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <ScheduleIcon sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6">{service.timeline}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Timeline may vary based on organization size and complexity. We'll provide a detailed project schedule during the initial consultation.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Ready to Get Started?
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Contact our infrastructure experts to discuss your specific requirements and get a customized proposal.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              sx={{ 
                py: 1.5, 
                px: 4,
                animation: `${pulsate} 2s ease-in-out infinite`,
                '&:hover': {
                  animation: 'none',
                  transform: 'scale(1.05)',
                }
              }}
            >
              Get Quote Now!
            </Button>
          </Box>
        </Paper>
      </Container>
      
      {/* Contact Form Section */}
      <Contact />
    </Box>
  );
};

export default ServiceDetails;